<template>
  <div class="app-shell">
    <v-navigation-drawer
      v-model="drawer"
      app
      :permanent="!isMobile"
      :temporary="isMobile"
      :mini-variant="collapsed"
      mini-variant-width="80"
      width="272"
      class="shell-drawer"
      :class="{ 'shell-drawer--mini': collapsed }"
    >
      <div class="drawer-inner">
        <div class="drawer-brand">
          <img src="../../assets/Images/pnglogo.png" alt="" class="brand-logo" />
          <div v-if="!collapsed" class="brand-text">
            <div class="brand-name">NATURESTAY</div>
            <div class="brand-panel">{{ panelLabel }}</div>
          </div>
          <v-btn
            v-if="isMobile"
            icon
            small
            class="ml-auto"
            aria-label="Close menu"
            @click="drawer = false"
          >
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </div>

        <nav class="drawer-nav">
          <div v-for="group in navGroups" :key="group.title || 'main'" class="nav-group">
            <div v-if="group.title && !collapsed" class="nav-section">{{ group.title }}</div>
            <div v-else-if="group.title" class="nav-divider"></div>
            <template v-for="item in group.items">
              <!-- Expandable group (e.g. Users List) -->
              <div v-if="item.children" :key="item.name" class="nav-parent">
                <v-tooltip right :disabled="!collapsed" content-class="nav-tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <button
                        type="button"
                        class="nav-item nav-item--parent"
                        :class="{ 'nav-item--parent-active': groupHasActive(item) }"
                        :aria-expanded="String(isGroupOpen(item))"
                        @click="toggleGroup(item)"
                      >
                        <v-icon class="nav-icon">{{ item.icon }}</v-icon>
                        <span class="nav-label">{{ item.name }}</span>
                        <v-icon v-if="!collapsed" class="nav-chevron">
                          {{ isGroupOpen(item) ? "mdi-chevron-up" : "mdi-chevron-down" }}
                        </v-icon>
                      </button>
                    </div>
                  </template>
                  <span>{{ item.name }}</span>
                </v-tooltip>
                <div v-show="isGroupOpen(item) && !collapsed" class="nav-children">
                  <router-link
                    v-for="child in item.children"
                    :key="child.route"
                    :to="child.route"
                    class="nav-item nav-item--child"
                    :class="{ 'nav-item--active': isActive(child) }"
                  >
                    <v-icon class="nav-icon">{{ child.icon }}</v-icon>
                    <span class="nav-label">{{ child.name }}</span>
                  </router-link>
                </div>
              </div>

              <v-tooltip
                v-else
                :key="item.route"
                right
                :disabled="!collapsed"
                content-class="nav-tooltip"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <router-link
                      :to="item.route"
                      class="nav-item"
                      :class="{ 'nav-item--active': isActive(item) }"
                    >
                      <v-icon class="nav-icon">{{ item.icon }}</v-icon>
                      <span class="nav-label">{{ item.name }}</span>
                    </router-link>
                  </div>
                </template>
                <span>{{ item.name }}</span>
              </v-tooltip>
            </template>
          </div>
        </nav>

        <div class="drawer-footer">
          <div class="user-card">
            <div class="user-avatar">{{ userInitials }}</div>
            <div v-if="!collapsed" class="user-meta">
              <div class="user-name">{{ userName }}</div>
              <div class="user-role">{{ userRole }}</div>
            </div>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="user-logout"
                  :class="{ 'ml-auto': !collapsed }"
                  aria-label="Logout"
                  v-bind="attrs"
                  v-on="on"
                  @click="appLogout"
                >
                  <v-icon small color="white">mdi-logout</v-icon>
                </v-btn>
              </template>
              <span>Logout</span>
            </v-tooltip>
          </div>
        </div>
      </div>
    </v-navigation-drawer>

    <AppHeader
      :title="pageTitle"
      :subtitle="panelLabel"
      :is-mobile="isMobile"
      :mini="mini"
      :user-name="userName"
      :user-role="userRole"
      :user-initials="userInitials"
      @toggle-sidebar="toggleMini"
      @logout="appLogout"
    />

    <div class="shell-content mainbg4" :class="{ 'shell-content--mobile': isMobile }">
      <router-view></router-view>
    </div>

    <nav v-if="isMobile" class="bottom-nav" aria-label="Quick navigation">
      <router-link
        v-for="item in bottomItems"
        :key="item.route"
        :to="item.route"
        class="bottom-nav-item"
        :class="{ 'bottom-nav-item--active': isActive(item) }"
      >
        <span class="bottom-nav-icon"><v-icon>{{ item.icon }}</v-icon></span>
        <span class="bottom-nav-label">{{ item.short || item.name }}</span>
      </router-link>
      <button type="button" class="bottom-nav-item" @click="drawer = true">
        <span class="bottom-nav-icon"><v-icon>mdi-menu</v-icon></span>
        <span class="bottom-nav-label">Menu</span>
      </button>
    </nav>
  </div>
</template>
<script>
import AppHeader from "@/components/Common/appHeader";
import { getUserType, getNavItems, flattenNavItems, panelLabels, getRoleDisplayName } from "@/components/Common/navConfig";
import { getStoredUsername, USERNAME_EVENT } from "@/utils/userName";

const MINI_KEY = "sidebarMini";

export default {
  components: {
    AppHeader,
  },
  data() {
    let mini = false;
    try {
      mini = localStorage.getItem(MINI_KEY) === "true";
    } catch (e) {
      mini = false;
    }
    return {
      drawer: !this.$vuetify.breakpoint.smAndDown,
      mini,
      // Groups the user opened or closed by hand, keyed by group name
      openGroups: {},
      storedUsername: getStoredUsername(),
    };
  },
  mounted() {
    window.addEventListener(USERNAME_EVENT, this.onUsernameChanged);
  },
  beforeDestroy() {
    window.removeEventListener(USERNAME_EVENT, this.onUsernameChanged);
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    collapsed() {
      return this.mini && !this.isMobile;
    },
    userType() {
      return getUserType();
    },
    panelLabel() {
      return panelLabels[this.userType];
    },
    navItems() {
      return getNavItems(this.userType);
    },
    navGroups() {
      const groups = [];
      this.navItems.forEach((item) => {
        const title = item.section || null;
        let group = groups.find((g) => g.title === title);
        if (!group) {
          group = { title, items: [] };
          groups.push(group);
        }
        group.items.push(item);
      });
      return groups;
    },
    bottomItems() {
      return flattenNavItems(this.navItems).filter((item) => item.bottom).slice(0, 4);
    },
    pageTitle() {
      const current = flattenNavItems(this.navItems).find((item) => this.isActive(item));
      if (current) return current.name;
      const name = this.$route.name || "";
      return name
        .replace(/[-_]/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    userName() {
      return this.storedUsername || "User";
    },
    userRole() {
      return getRoleDisplayName() || localStorage.getItem("role") || this.panelLabel;
    },
    userInitials() {
      const parts = this.userName.trim().split(/\s+/).filter(Boolean);
      const initials = parts
        .slice(0, 2)
        .map((p) => p.charAt(0))
        .join("");
      return (initials || "U").toUpperCase();
    },
  },
  watch: {
    isMobile(value) {
      this.drawer = !value;
    },
    $route() {
      if (this.isMobile) this.drawer = false;
      // Reveal the group that holds the page we just navigated to
      this.navItems.forEach((item) => {
        if (item.children && this.groupHasActive(item)) this.$set(this.openGroups, item.name, true);
      });
    },
  },
  methods: {
    onUsernameChanged(e) {
      this.storedUsername = (e && e.detail) || getStoredUsername();
    },
    isActive(item) {
      return this.$route.path === item.route;
    },
    groupHasActive(group) {
      return group.children.some((child) => this.isActive(child));
    },
    isGroupOpen(group) {
      const manual = this.openGroups[group.name];
      return manual !== undefined ? manual : this.groupHasActive(group);
    },
    toggleGroup(group) {
      // In the collapsed rail there is no room for the children, so widen it first
      const wasCollapsed = this.collapsed;
      if (wasCollapsed) this.toggleMini();
      this.$set(this.openGroups, group.name, wasCollapsed ? true : !this.isGroupOpen(group));
    },
    toggleMini() {
      this.mini = !this.mini;
      try {
        localStorage.setItem(MINI_KEY, String(this.mini));
      } catch (e) {
        // Collapsed state is only a convenience; ignore storage failures.
      }
    },
    async appLogout() {
      // Let an open application form save its draft before the session is cleared,
      // but don't hold up the logout for more than a few seconds.
      const pending = [];
      this.$root.$emit("before-logout", pending);
      await Promise.race([Promise.all(pending), new Promise((resolve) => setTimeout(resolve, 5000))]);
      this.$store.commit("logoutUser", true);
    },
  },
};
</script>

<style scoped>
.app-shell {
  min-height: 100vh;
}

/* ---------- Drawer ---------- */
.shell-drawer {
  background: linear-gradient(180deg, #0c4f4c 0%, #13736f 60%, #1b857f 100%) !important;
  border: none !important;
  box-shadow: 4px 0 24px rgba(15, 23, 42, 0.12);
}

.drawer-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.drawer-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 18px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.shell-drawer--mini .drawer-brand {
  justify-content: center;
  padding: 18px 0 16px;
}

.brand-logo {
  width: 42px;
  height: 42px;
  object-fit: contain;
  flex-shrink: 0;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.2));
}

.brand-name {
  font-size: 19px;
  font-weight: 900;
  color: #ffffff;
  letter-spacing: 1px;
  line-height: 1.1;
}

.brand-panel {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.65);
}

.drawer-nav {
  flex: 1;
  overflow-y: auto;
  padding: 10px 12px 16px;
}

.drawer-nav::-webkit-scrollbar {
  width: 4px;
}

.drawer-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.nav-section {
  margin: 16px 10px 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgba(255, 255, 255, 0.5);
}

.nav-group:first-child .nav-section {
  margin-top: 6px;
}

.nav-divider {
  height: 1px;
  margin: 12px 14px;
  background: rgba(255, 255, 255, 0.12);
}

.nav-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 0 12px;
  margin-bottom: 2px;
  border-radius: 12px;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.82);
  transition: background-color 0.2s ease, color 0.2s ease;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.nav-icon {
  color: inherit !important;
  font-size: 21px !important;
  flex-shrink: 0;
}

.nav-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-item--active,
.nav-item--active:hover {
  background: #ffffff;
  color: #13736f;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
}

.nav-item--active .nav-label {
  font-weight: 800;
}

.nav-item--active::before {
  content: "";
  position: absolute;
  left: -12px;
  top: 10px;
  bottom: 10px;
  width: 4px;
  border-radius: 0 4px 4px 0;
  background: #fbbf24;
}

.nav-item--parent {
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}

.nav-item--parent .nav-label {
  flex: 1;
}

.nav-item--parent-active {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.08);
}

.nav-chevron {
  color: inherit !important;
  font-size: 20px !important;
  margin-left: auto;
}

.nav-children {
  margin: 2px 0 6px 20px;
  padding-left: 10px;
  border-left: 1px solid rgba(255, 255, 255, 0.18);
}

.nav-item--child {
  min-height: 38px;
  gap: 10px;
}

.nav-item--child .nav-icon {
  font-size: 18px !important;
}

.nav-item--child .nav-label {
  font-size: 13px;
}

.nav-item--child.nav-item--active::before {
  display: none;
}

.shell-drawer--mini .drawer-nav {
  padding: 10px 14px 16px;
}

.shell-drawer--mini .nav-item {
  justify-content: center;
  padding: 0;
}

.shell-drawer--mini .nav-label {
  display: none;
}

.shell-drawer--mini .nav-item--active::before {
  left: -14px;
}

.drawer-footer {
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.user-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.08);
}

.shell-drawer--mini .user-card {
  flex-direction: column;
  padding: 10px 0;
  background: transparent;
}

.user-avatar {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fbbf24;
  color: #0c4f4c;
  font-size: 14px;
  font-weight: 800;
}

.user-meta {
  min-width: 0;
}

.user-name,
.user-role {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-name {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
}

.user-role {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.user-logout {
  background: rgba(239, 68, 68, 0.25);
}

.user-logout:hover {
  background: rgba(239, 68, 68, 0.6);
}

/* ---------- Content ---------- */
.shell-content {
  min-height: calc(100vh - 68px);
}

.shell-content--mobile {
  min-height: calc(100vh - 58px);
  padding-bottom: calc(72px + env(safe-area-inset-bottom));
}

/* ---------- Mobile bottom bar ---------- */
.bottom-nav {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  display: flex;
  padding: 6px 6px calc(6px + env(safe-area-inset-bottom));
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(12px);
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.08);
}

.bottom-nav-item {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 4px 2px;
  border: none;
  background: none;
  cursor: pointer;
  text-decoration: none;
  color: #64748b;
  font-family: inherit;
}

.bottom-nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 30px;
  border-radius: 999px;
  transition: background-color 0.2s ease;
}

.bottom-nav-icon .v-icon {
  color: inherit !important;
  font-size: 22px !important;
}

.bottom-nav-label {
  max-width: 100%;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bottom-nav-item--active {
  color: #13736f;
}

.bottom-nav-item--active .bottom-nav-icon {
  background: rgba(19, 115, 111, 0.12);
}

.bottom-nav-item--active .bottom-nav-label {
  font-weight: 800;
}
</style>

<style>
/* Shared legacy helpers used by pages across the app. */
.activated {
  color: white !important;
}

.demo-bg {
  opacity: 0.6;
}

.mainbg2 {
  background-image: linear-gradient(269.6deg, #e7f1f0 -31.66%, #e7f1f0);
}

.item-text {
  display: flex;
  align-items: center;
  font-size: 18px;
  color: black;
  cursor: pointer;
  font-family: var(--font-body);
  margin-left: 8px;
}

.content {
  height: 40%;
}

.mainbg5 {
  background-image: linear-gradient(269.6deg, #e7f1f0 -31.66%, #e7f1f0);
  padding: 5px;
}

.mainbg6 {
  background-image: linear-gradient(269.6deg, #e7f1f0 -31.66%, #e7f1f0);
  padding: 5px;
}

.activated .content {
  background-color: #f5f5f5;
}

.activated .mainbg6 {
  background-color: #eeeeee;
}

.nav-tooltip {
  font-weight: 600;
}
</style>
